/**
 * GameRuleset — the entire tunable surface of the game's rules and
 * scoring, in one typed schema.
 *
 * Phase 1 of the GameRuleset roadmap (StackedStreak-dgg). This file
 * is P1.1 (StackedStreak-c3t): it defines the type shape and exports
 * DEFAULT_POKER_RULESET — values that match today's hardcoded
 * behavior 1:1. P1.2 will update HandEvaluator / BoardGenerator /
 * StreakManager / useGameState to read from a ruleset instead of
 * their local constants; until then, nothing in the engine actually
 * consumes this file. Importing it here is a no-op.
 *
 * Design constraint: the shape must be expressive enough to support
 * non-poker grid games (word-grid, connected-region) per the Phase 5
 * white-label work. The MatchRule discriminated union is the seam
 * for that future work — only PokerHandRule is implemented in
 * Phase 1; the WordRule / RegionRule sketches below are intentional
 * placeholders that document where they slot in.
 */

import type { HandName } from './game';
import { HAND_SCORES } from './game';

// ────────────────────── Match rules (discriminated union) ──────────────────────

/**
 * The PokerBlitz match rule: 5-card poker-hand evaluation against a
 * scored hand-rank table.
 */
export interface PokerHandRule {
  kind: 'poker-hand';
  /** Score awarded for each detected poker hand. Mirrors HAND_SCORES. */
  handScores: Record<HandName, number>;
  /**
   * Which poker hand types the engine should detect. Excluding a hand
   * from this list makes it un-scoreable for this ruleset (a "no-pairs"
   * variant would set this to everything except 'pair' and 'high-card').
   */
  handsEnabled: HandName[];
}

/**
 * Word-grid match rule (P5.4 forcing-function PoC,
 * StackedStreak-a0m). Players select N adjacent cells, the engine
 * concatenates their values into a word and validates it against
 * a dictionary. Different scoring math than poker — letter-value
 * sum × length-bonus rather than hand-rank-table lookup.
 *
 * The dictionary itself is loaded by the WordEvaluator module
 * (src/lib/game-logic/word/WordEvaluator.ts); this rule shape just
 * names which dictionary the variant uses + the score
 * coefficients. Coefficients live in data so an admin can tune
 * "rare-letters worth more" or "longer-words worth more" without
 * a code change — exactly the same lever as PokerHandRule.handScores.
 */
export interface WordMatchRule {
  kind: 'word-match';
  /** Dictionary identifier; engine resolves to a word list. */
  dictionary: string;
  /** Minimum word length to score. Words shorter than this are
   *  rejected (treated as no-match). */
  minLength: number;
  /** Minimum word length that ADVANCES the combo streak (StackedStreak-77l5).
   *  Shorter words still score — they're the mop-up/strategy layer — but
   *  contribute 0 streak steps, so single-letter spam can't farm the
   *  multiplier. Raised to 4 in round 6 (StackedStreak-6km0): with the
   *  combo now stepping up on the FIRST eligible word, 3-letter words fed
   *  it too cheaply. */
  streakMinWordLength: number;
  /**
   * Combo multiplier by accumulated charge (round 6, StackedStreak-6km0).
   * Index = charge, value = multiplier; the last entry applies to every
   * higher charge. Was a hardcoded tier function — the round-6 playtest
   * needed the ramp retuned, which is exactly the kind of number that
   * belongs in the ruleset.
   *
   * [1, 2, 3, 4, 5] ⇒ no words ×1, and every eligible word thereafter
   * steps the multiplier up one, topping out at ×5. The old table needed
   * TWO words before the multiplier moved at all, which read as broken.
   */
  comboTiers: number[];
  /** Score per letter, keyed by lowercase letter — Scrabble-like
   *  rarity weighting ('q'=10, 'a'=1, etc.). */
  letterScores: Record<string, number>;
  /** Bonus multiplier per length tier. Keys are word lengths,
   *  values are multipliers applied to the letter-score sum. */
  lengthBonuses: Record<number, number>;

  // ── Daily-mode round-end scoring (nkr1.4, proposal §2.3) ──
  // These apply only when the board is a finite letter set (daily mode);
  // endless/practice rounds bank words with no round-end adjustment.

  /**
   * How a leftover tile is priced at the buzzer (round 6,
   * StackedStreak-6km0 — the flat 10/tile read as "the penalty ate my
   * score", and the %-cap was firing on ordinary runs):
   *
   *   'letter-value' (default) — each stranded tile costs its OWN
   *      letterScores value. Averages ~1.9/tile on an English board, and
   *      it teaches the right lesson: leaving the Q behind hurts, leaving
   *      an E behind barely registers.
   *   'flat' — every tile costs unusedLetterPenalty, whatever it is.
   *      The round-5 behaviour; kept for the A/B (?wbvariant=penalty-flat1).
   */
  unusedPenaltyMode: 'letter-value' | 'flat';
  /** Points subtracted per letter still on the board at timeout in 'flat'
   *  mode (positive magnitude, e.g. 25 ⇒ −25/letter). Ignored by
   *  'letter-value' mode, which prices each tile individually. */
  unusedLetterPenalty: number;

  /**
   * Maximum points for PARTIAL board progress, paid in proportion to the
   * tiles you removed (round 10, 2026-08-06).
   *
   * WHY THIS EXISTS. Six days of live scores showed the leaderboard was not a
   * ladder, it was a pass/fail exam:
   *
   *     cleared a board   11 players   min 220   median 498   max 1102
   *     cleared none      26 players   min   0   median   2   max   70
   *
   * NOT ONE PLAYER HAS EVER SCORED BETWEEN 71 AND 219. The distribution is
   * bimodal with an empty band, because boardClearedBonus (100, doubling) is
   * worth more than every word a good player banks, so rank was almost
   * entirely "did you clear" rather than "how well did you play".
   *
   * That is what "the leaderboard feels flat" actually was: telling someone
   * they are "221 from 2nd" is meaningless when 221 is not a gap you close by
   * playing better, only by crossing a threshold.
   *
   * Emptying 30 of 36 tiles is genuinely good play and used to score ~2. Now
   * it scores most of this bonus. Set to 0 to restore the old cliff.
   */
  boardProgressBonus?: number;
  /** Bonus banked for the FIRST board cleared in a round. Subsequent
   *  boards in the chain escalate by clearedBonusGrowth (round 5 —
   *  StackedStreak-3ylw): clearing keeps the round going, so speed is
   *  rewarded by reaching more boards, each worth more. */
  boardClearedBonus: number;
  /** Clear-bonus escalation per chained board: board N's clear banks
   *  boardClearedBonus × growth^(N−1). 2 = doubling (user's call). */
  clearedBonusGrowth: number;
  /** DEPRECATED (round 5): the time-saved multiplier died with
   *  end-on-clear — the board chain rewards speed instead. Field kept
   *  so stored rulesets/fixtures don't break; no longer applied. */
  timeSavedMultiplierPerSec: number;
  /** Cap on the unused-letter penalty as a percentage of the banked
   *  score (round 5): penalty = min(tiles × unusedLetterPenalty,
   *  banked × this / 100). A real run can never floor to 0 — the
   *  playtest leaderboard read 'score not recording'. */
  unusedPenaltyMaxPctOfBanked: number;
  /** Reshuffles available per game (rearrange remaining tiles, never
   *  swaps letters — mechanic lands in nkr1.5; the allowance is data). */
  reshufflesPerGame: number;
  /** Using Shuffle resets the combo (StackedStreak-rave, user rule). */
  shuffleBreaksCombo: boolean;
  /** Advancing to the next chained board resets the combo (rave). */
  nextBoardBreaksCombo: boolean;
  /** Word lengths the board packer may place (StackedStreak-g1b5).
   *  Daily boards are tiled from real dictionary words in this range.
   *  packingMinWordLength stays ≥3 even though minLength allows shorter
   *  play (77l5): boards are BUILT from real words; short words are a
   *  player tool, not construction material. */
  packingMinWordLength: number;
  packingMaxWordLength: number;

  /**
   * Medal thresholds as a percentage of the day's par (round 7).
   *
   * Replaces a bare "Echidna's par — 190" number, which meant nothing
   * without a mascot the game hasn't built yet, and gave a single
   * pass/fail target most players would never reach. Medals are
   * self-explanatory with no lore, and turn one unreachable goal into
   * three reachable ones. Descending order; the first threshold met wins.
   */
  parMedals: Array<{ name: string; pct: number }>;

  /** Prototype mechanic flags (nkr1.9) — see WordVariantFlags. */
  variants: WordVariantFlags;
}

/**
 * Prototype mechanic flags (nkr1.9, docs/ECHIDNA_MECHANICS_RESEARCH.md).
 * Every mechanic is OFF by default and individually toggleable so the P1.8
 * friends-and-family prototype can bucket testers per mechanic. All values
 * are data — tune in the ruleset, never in code.
 */
export interface WordVariantFlags {
  /**
   * Spatial word-chaining (research Part B, V1 "hot-zone glow"): after a
   * word clears, tiles adjacent to the cleared region glow for
   * chainWindowSec; starting the next word on a glowing tile advances the
   * streak by extra steps. Feeds the EXISTING combo multiplier — never a
   * separate scoring currency (research Part D anti-pattern).
   */
  chainEnabled: boolean;
  /**
   * 'hot-zone'  V1: next word starts adjacent to the cleared region.
   * 'last-tile' V2: next word starts adjacent to the PREVIOUS word's final
   *                 tile only (stricter spatial shiritori).
   * 'lexical'   V3: next word STARTS WITH the previous word's last letter
   *                 (no spatial component — the legibility fallback).
   */
  chainMode: 'hot-zone' | 'last-tile' | 'lexical';
  /** Seconds the chain opportunity (glow) stays open after a clear. */
  chainWindowSec: number;
  /** Extra streak steps a chained word grants on top of the normal +1. */
  chainStreakBonus: number;

  /**
   * Post-game "what you missed" reveal (research mechanic #4): top missed
   * words and "you found X of Y". Frame as the board keeping secrets, not
   * the player failing (research Part D).
   */
  revealEnabled: boolean;
  /** How many missed words to show, best-scoring first. */
  revealTopMissed: number;

  /**
   * Named rank ladder (research mechanic #2): the round's score as a
   * percentage of the board's total word-score pool, mapped to tier names.
   */
  ranksEnabled: boolean;
  /**
   * Tiers in ascending order of pct (score as % of the board's word-score
   * pool required to reach the tier). The highest reached tier is shown.
   */
  rankTiers: Array<{ name: string; pct: number }>;

  /**
   * Combo multiplier mode (StackedStreak-rave A/B —
   * docs/ECHIDNA_MULTIPLIER_RESEARCH.md):
   *   'streak-tiers'  A (default): +1 charge per eligible word, tier table.
   *   'decay-timer'   B: A plus charge −1 per comboDecaySec idle.
   *   'length-fueled' C: charge += word length; a tier every
   *                   comboChargeDivisor charge.
   */
  comboMode: 'streak-tiers' | 'decay-timer' | 'length-fueled';
  /** Idle seconds per charge point drained ('decay-timer' only). */
  comboDecaySec: number;
  /** Charge per multiplier step ('length-fueled' only). */
  comboChargeDivisor: number;

  /**
   * Proximity scoring (round 6, StackedStreak-6km0 — the user's "letters
   * near where a word has just been cleared should be worth more").
   *
   * Where chainEnabled rewards proximity with COMBO CHARGE, this rewards it
   * with POINTS: each tile of the new word that sits in the previous word's
   * hot zone adds proximityBonusPct% to that word's score. The two stack
   * deliberately — they are separate levers on the same spatial idea, and
   * the playtest should be able to feel them apart.
   *
   * Off by default; enable with ?wbvariant=proximity.
   */
  proximityEnabled: boolean;
  /** Percent added to the word score per tile inside the hot zone. */
  proximityBonusPct: number;
  /** Cap on the total proximity bonus, as a percent of the word score. */
  proximityMaxPct: number;

  /**
   * Blind submit (round 6, StackedStreak-6km0). Today Submit is disabled
   * until the traced path IS a word, so "an invalid submit resets your
   * combo" describes a state the player cannot reach — there are no wrong
   * answers. This variant hides the live verdict and lets any path be
   * submitted; a miss costs the combo.
   *
   * Raises the skill ceiling and makes the combo a real risk/reward
   * decision, at the obvious cost of frustrating players who were relying
   * on the game to spell-check them. Strictly a playtest arm — enable with
   * ?wbvariant=blind.
   */
  blindSubmitEnabled: boolean;

  /**
   * Themed daily boards (round 6, StackedStreak-6km0). The board is tiled
   * from one theme's words ("Dog breeds") instead of arbitrary dictionary
   * words. The player is told the theme and how many words hide in it, but
   * never which — so the AI par stops listing its solution, and the results
   * screen reveals found words in full and unfound ones as blanks.
   *
   * ON by default (the user's call, round 6). Note that changing this
   * changes what board a given daily seed produces, so stored
   * echidna_daily_challenges rows must be regenerated whenever it flips —
   * REGENERATE=1 on ensure-upcoming-word-challenges.ts (StackedStreak-j7xq).
   * Disable for an A/B arm with ?wbvariant=notheme.
   */
  themedBoardsEnabled: boolean;

  /**
   * What the player pays to replay (round 6, StackedStreak-6km0).
   *
   *   'none'   — free replay (pre-beta behaviour).
   *   'survey' — one beta survey question, skippable. The question
   *              occupies exactly the slot a rewarded ad will, so this is
   *              the beta standing in for monetisation while also buying
   *              us the feedback we need to finish the game.
   *   'ad'     — a rewarded ad through the existing RewardedAdButton /
   *              grantAdReward path.
   *
   * The point of the enum is that ending the beta is a config change, not
   * a rewrite: the gate component and the replay call site stay put, only
   * the reward source swaps. Override for testing with
   * ?wbvariant=gate-none / gate-survey.
   */
  replayGate: 'none' | 'survey' | 'ad';

  /**
   * Hide the theme's NAME during play (round 6, StackedStreak-6km0 — the
   * user's call: "the theme should be hidden, that was the original
   * point").
   *
   * The blanks stay visible, which is what makes this work: seven unfound
   * words of known lengths are a puzzle, and naming the theme up front
   * solves it for you. Hidden, the theme becomes a second thing to figure
   * out alongside clearing the board.
   *
   * NOTE: "revealed at the buzzer" was the round-6 behaviour and it was
   * WRONG — see themeRevealAfter.
   */
  themeHidden: boolean;

  /**
   * How many theme words you must find before the theme's NAME is given to
   * you (round 9, StackedStreak-nkr1.23).
   *
   * ─── The bug this fixes ───
   *
   * The results screen used to print "TODAY'S THEME · DOGS" unconditionally,
   * even on a 0/6 round. That is not a spoiler at the end of a puzzle — it is
   * a spoiler at the START of the next one, because the theme hunt is
   * DAY-cumulative (themeHunt.ts): the same theme is live for every replay
   * until midnight. One bad round handed over the answer for the whole day,
   * and the game then kept asking "ALL ABOUT ______" of a player who had
   * already been told.
   *
   * ─── The rule ───
   *
   * The name is earned, on the same terms everywhere it can appear — in
   * round, on the results screen, on a replay. Below the threshold every
   * surface shows the theme's PROMPT and the blanks, which is the shape of
   * what you missed without the answer; at or above it, the name.
   *
   * Counted DAY-cumulatively, not per round, because the puzzle is the day.
   * Two rounds of one find each have earned it as much as one round of two.
   *
   * Set to 0 to reveal always (what round 6 shipped), or above the theme
   * size to never reveal.
   */
  themeRevealAfter: number;

  /**
   * Is the combo multiplier live? (round 7)
   *
   * Turned OFF after playtest: "I don't think the multiplier adds much
   * value and actually slows the game down. The even scoring works." It
   * made word ORDER strategic, which was good for a leaderboard, but the
   * cost was thinking time in a 90-second game that wants you tracing.
   *
   * Kept behind a flag rather than deleted — the scoring path, the tier
   * table and the par calculation all still work, so re-enabling is one
   * boolean if the beta survey says players miss it.
   * ?wbvariant=combo turns it back on.
   */
  comboEnabled: boolean;

  /**
   * Minimum live play-streak before the results screen pitches an account
   * (R10, StackedStreak-kfy2 — expert recommendation, owner-approved).
   *
   * The pitch is loss-aversion framed ("keep your streak on every
   * device"), and loss aversion needs something to lose: shown to a
   * player with a 0-day streak it is just a sign-up nag on a game that is
   * proudly playable without an account. Three days is the point where
   * the streak is an asset worth protecting and the pitch becomes true.
   *
   * The menu → profile route is ungated — this only gates the RESULTS
   * SCREEN pitch. Signed-in players never see it (their account UI lives
   * on the profile).
   */
  accountPitchMinStreak: number;
}

// Color/region rule sketch — not implemented yet; left in comments
// so a future Phase 5 follow-up has a clear seam.
//
// export interface ConnectedRegionRule {
//   kind: 'connected-region';
//   minSize: number;
//   scoreFn: (size: number, color: string) => number;
// }

/** Discriminated union slot for game-kinds. P5.4 added WordMatchRule. */
export type MatchRule = PokerHandRule | WordMatchRule; // | ConnectedRegionRule

/**
 * GameRuleset narrowed to the poker variant — every field of
 * GameRuleset, but matchRule is fixed to PokerHandRule. Used to
 * type DEFAULT_POKER_RULESET below so call sites that read
 * `matchRule.handScores` / `.handsEnabled` don't need to narrow
 * the discriminated union manually. New variants get their own
 * narrowed alias when they ship.
 */
export type PokerGameRuleset = Omit<GameRuleset, 'matchRule'> & {
  matchRule: PokerHandRule;
};

/**
 * GameRuleset narrowed to the Echidna variant — every field of
 * GameRuleset, but matchRule is fixed to WordMatchRule. Parallels
 * PokerGameRuleset so word-grid call sites that read
 * `matchRule.letterScores` / `.lengthBonuses` / `.minLength` don't have
 * to narrow the discriminated union by hand. Used to type
 * ECHIDNA_RULESET below.
 */
export type WordGameRuleset = Omit<GameRuleset, 'matchRule'> & {
  matchRule: WordMatchRule;
};

// ────────────────────────── Tunable sections ──────────────────────────

export interface GridConfig {
  /** Board height in cards/cells. */
  rows: number;
  /** Board width in cards/cells. */
  cols: number;
}

export interface TimingConfig {
  /** Total game duration in seconds (90 in Classic Mode today). */
  gameDurationSec: number;
  /** Streak timer maximum — caps the decay window. */
  streakTimerMaxSec: number;
  /** Streak begins decaying when timer drops at or below this many seconds. */
  streakDecayThresholdSec: number;
  /** Bonus seconds added on a "Plus One" (same-hand-repeated) match. */
  streakBonusOnPlusOneSec: number;
}

export interface ScoringConfig {
  /** Multiplier value the moment a streak activates. */
  multiplierOnActivation: number;
  /** Hard cap on multiplier; growth saturates here. */
  multiplierMax: number;
  /**
   * Growth rule when stacking a NEW hand into an existing streak
   * (different hand than the currently-stacked one).
   *   'double'    → currentMultiplier × 2 (current PokerBlitz behavior).
   *   'increment' → currentMultiplier + 1 (gentler curve).
   */
  multiplierStackGrowth: 'double' | 'increment';
  /**
   * Growth on a "Plus One" (same hand repeated within an active streak).
   *   'increment' → currentMultiplier + 1 (current behavior).
   *   'double'    → reserved for variants where repeats are explosive.
   */
  multiplierPlusOneGrowth: 'double' | 'increment';
}

export interface ModifierSpawnConfig {
  /** Per-card spawn probability for a "golden" modifier (score doubler). */
  goldenPct: number;
  /** Maximum number of golden modifier cards on a single board. */
  goldenCap: number;
  /** Per-card spawn probability for a "locked" modifier. */
  lockedPct: number;
  /** Maximum number of locked modifier cards on a single board. */
  lockedCap: number;
  /** Number of hand-completions before a locked card unlocks (and converts to golden). */
  lockedUnlockAfter: number;
  /** Per-card spawn probability for a "wild" modifier (any suit/value). */
  wildPct: number;
  /** Maximum number of wild modifier cards on a single board. */
  wildCap: number;
}

// ─────────────────────────── The full ruleset ───────────────────────────

export interface GameRuleset {
  /**
   * Stable identifier for this ruleset. For DEFAULT_POKER_RULESET this
   * is a slug; for DB-backed rulesets (Phase 1.3) this will be the
   * Supabase row UUID. Used for stamping game_sessions in Phase 1.4
   * so an in-flight game keeps its rules even if an admin publishes
   * a new ruleset mid-session.
   */
  id: string;
  /** Human-readable name (e.g., "PokerBlitz Classic v1"). */
  name: string;
  /**
   * Schema version — bump when the SHAPE of GameRuleset changes (not
   * when the values change). Loaders should refuse rulesets with a
   * schemaVersion they don't understand rather than silently coercing.
   */
  schemaVersion: 1;
  /**
   * Game-kind tag. 'poker_blitz' is the only kind in Phase 1; Phase 5
   * introduces additional kinds (word_grid, color_region, …) which
   * pair with their respective MatchRule discriminants.
   */
  gameKind: string;
  grid: GridConfig;
  timing: TimingConfig;
  scoring: ScoringConfig;
  modifierSpawn: ModifierSpawnConfig;
  matchRule: MatchRule;
}

// ───────────── Default ruleset — exact match for today's behavior ─────────────

/**
 * Stable UUID for the seed PokerBlitz ruleset. The Supabase seed row
 * (P1.3) is inserted with this exact id so DEFAULT_POKER_RULESET.id
 * and the DB row's id always agree — that lets P1.4 stamp
 * game_sessions.ruleset_id with this UUID even when the client is
 * offline and falls back to the in-memory default.
 *
 * DO NOT CHANGE — changing this would orphan every game_session row
 * stamped with the old value.
 */
export const DEFAULT_POKER_RULESET_ID = '00000000-0000-0000-0000-00000000c1a5';


/**
 * The single source of truth for "what the game's rules are right now."
 * Every value here corresponds to a hardcoded constant somewhere in the
 * engine TODAY:
 *
 *   grid.rows / grid.cols              src/lib/game-logic/BoardGenerator.ts
 *                                       — outer loops `for row=0; row<6; row++`,
 *                                         `for col=0; col<5; col++`
 *
 *   timing.gameDurationSec             src/lib/hooks/useGameState.ts:148
 *                                       — `setGameTime(targetMode === 'daily-challenge' ? 0 : 90)`
 *
 *   timing.streakTimerMaxSec           src/lib/game-logic/StreakManager.ts:7
 *                                       — `private readonly MAX_TIMER = 15`
 *
 *   timing.streakDecayThresholdSec     src/lib/game-logic/StreakManager.ts:9
 *                                       — `private readonly DECAY_THRESHOLD = 5`
 *
 *   timing.streakBonusOnPlusOneSec     src/lib/game-logic/StreakManager.ts:92
 *                                       — `const bonusTime = 2`
 *
 *   scoring.multiplierOnActivation     src/lib/game-logic/StreakManager.ts:37
 *                                       — `this.state.currentMultiplier = 2`
 *
 *   scoring.multiplierMax              src/lib/game-logic/StreakManager.ts:8
 *                                       — `private readonly MAX_MULTIPLIER = 100`
 *
 *   scoring.multiplierStackGrowth      src/lib/game-logic/StreakManager.ts:68
 *                                       — `currentMultiplier * 2` ⇒ 'double'
 *
 *   scoring.multiplierPlusOneGrowth    src/lib/game-logic/StreakManager.ts:91
 *                                       — `currentMultiplier + 1` ⇒ 'increment'
 *
 *   modifierSpawn.golden{Pct,Cap}      src/lib/game-logic/BoardGenerator.ts:37
 *                                       — `roll < 0.05 && goldenCount < 2`
 *
 *   modifierSpawn.locked{Pct,Cap}      src/lib/game-logic/BoardGenerator.ts:40
 *                                       — `roll < 0.08 && lockedCount < 1`
 *                                         (0.08 - 0.05 = 0.03 effective rate)
 *
 *   modifierSpawn.lockedUnlockAfter    src/lib/game-logic/BoardGenerator.ts:42
 *                                       — `card.lockedFor = 2`
 *
 *   modifierSpawn.wild{Pct,Cap}        src/lib/game-logic/BoardGenerator.ts:44
 *                                       — `roll < 0.09 && wildCount < 1`
 *                                         (0.09 - 0.08 = 0.01 effective rate)
 *
 *   matchRule.handScores               src/types/game.ts:108 — HAND_SCORES
 *
 * After Phase 1.2 ships, those hardcoded constants disappear from the
 * engine and the values come from here (or from a DB row matching this
 * shape). If you're tempted to change a number in this constant,
 * remember that until P1.3+P1.4 ship there's no admin override path —
 * a value change here is a behavior change for everyone.
 */
export const DEFAULT_POKER_RULESET: PokerGameRuleset = {
  id: DEFAULT_POKER_RULESET_ID,
  name: 'PokerBlitz Classic',
  schemaVersion: 1,
  gameKind: 'poker_blitz',
  grid: { rows: 6, cols: 5 },
  timing: {
    gameDurationSec: 90,
    streakTimerMaxSec: 15,
    streakDecayThresholdSec: 5,
    streakBonusOnPlusOneSec: 2,
  },
  scoring: {
    multiplierOnActivation: 2,
    multiplierMax: 100,
    multiplierStackGrowth: 'double',
    multiplierPlusOneGrowth: 'increment',
  },
  modifierSpawn: {
    goldenPct: 0.05,
    goldenCap: 2,
    lockedPct: 0.03,
    lockedCap: 1,
    lockedUnlockAfter: 2,
    wildPct: 0.01,
    wildCap: 1,
  },
  matchRule: {
    kind: 'poker-hand',
    handScores: HAND_SCORES,
    handsEnabled: [
      'royal-flush',
      'straight-flush',
      'four-of-a-kind',
      'full-house',
      'flush',
      'straight',
      'three-of-a-kind',
      'two-pair',
      'pair',
      'high-card',
    ],
  },
};

// ───────────── Echidna ruleset — the second engine variant ─────────────

/**
 * Stable id for the seed Echidna ruleset. Parallels
 * DEFAULT_POKER_RULESET_ID. Distinct UUID so a future DB seed row and
 * session-stamping can coexist with the poker default.
 */
export const ECHIDNA_RULESET_ID = '00000000-0000-0000-0000-0000000bb1a2';

/**
 * Echidna Classic — the Boggle-style variant on the PokerBlitz engine.
 *
 * Design choices:
 *   grid 6×6 (36 letters)    — widened from 4×4 per the nkr1.7 playtest
 *                              (StackedStreak-g1b5): ≥44px touch targets
 *                              hold down to 320px-wide devices with the
 *                              board's tightened mobile gaps, and the
 *                              word-packed generator wants room for
 *                              longer words.
 *   minLength 3              — standard Boggle floor; 2-letter words rejected.
 *   letterScores             — Scrabble English letter values (common
 *                              letters cheap, q/z = 10, j/x = 8, etc.). Same
 *                              "rarity in data" lever as PokerHandRule.handScores.
 *   lengthBonuses            — progressive multiplier rewarding longer words:
 *                              3→×1, 4→×1.2, 5→×1.5, 6→×2, 7+→×3. WordEvaluator
 *                              uses the highest tier ≤ word length, so 8-letter
 *                              words also get the ×3 tier.
 *   timing.gameDurationSec   90 — mirrors PokerBlitz Classic round length.
 *   scoring / modifierSpawn  — sensible defaults reusing the shared shapes.
 *                              goldenPct 0 keeps v1 free of board modifiers
 *                              (no golden/locked/wild letters yet); the fields
 *                              exist because GameRuleset requires them.
 *
 * The streak timing values (streakTimerMax / decay / plus-one bonus) are
 * carried over from the poker default unchanged so the shared StreakManager
 * behaves consistently across variants until Echidna tunes its own curve.
 */
export const ECHIDNA_RULESET: WordGameRuleset = {
  id: ECHIDNA_RULESET_ID,
  name: 'Echidna Classic',
  schemaVersion: 1,
  gameKind: 'word_grid',
  grid: { rows: 6, cols: 6 },
  timing: {
    gameDurationSec: 90,
    streakTimerMaxSec: 15,
    streakDecayThresholdSec: 5,
    streakBonusOnPlusOneSec: 2,
  },
  scoring: {
    multiplierOnActivation: 2,
    multiplierMax: 100,
    multiplierStackGrowth: 'increment',
    multiplierPlusOneGrowth: 'increment',
  },
  modifierSpawn: {
    // v1: no board modifiers on the letter grid. Caps are 0 too so nothing
    // can spawn even if a probability were nonzero.
    goldenPct: 0,
    goldenCap: 0,
    lockedPct: 0,
    lockedCap: 0,
    lockedUnlockAfter: 0,
    wildPct: 0,
    wildCap: 0,
  },
  matchRule: {
    kind: 'word-match',
    // v5 (StackedStreak-6km0): ENABLE ∩ common-frequency list, PLUS the
    // regular inflections of everything that survived (~58k). v4 rejected
    // WAGGED while accepting WAG/WAGS/WAGGING — the frequency list is
    // lemma-biased, and the player correctly read that as a bug. Still no
    // TZIGANES-style accepts; solver/par/reveals share the same list.
    dictionary: 'echidna-v5',
    // 77l5: single letters count ('a', 'i') — mop-up strategy for the
    // clear-the-board goal. Words under streakMinWordLength score but
    // don't build the combo.
    minLength: 1,
    // Round 6: 4+ letters to feed the combo (was 3), because the combo now
    // steps up on the very first eligible word rather than the second.
    streakMinWordLength: 4,
    comboTiers: [1, 2, 3, 4, 5],
    // Scrabble English letter values.
    letterScores: {
      a: 1, b: 3, c: 3, d: 2, e: 1, f: 4, g: 2, h: 4, i: 1, j: 8,
      k: 5, l: 1, m: 3, n: 1, o: 1, p: 3, q: 10, r: 1, s: 1, t: 1,
      u: 1, v: 4, w: 4, x: 8, y: 4, z: 10,
    },
    lengthBonuses: {
      3: 1,
      4: 1.2,
      5: 1.5,
      6: 2,
      7: 3,
      // Longer words exist by construction on word-packed boards (g1b5).
      8: 4,
      10: 5,
    },
    // Daily-mode round-end scoring (nkr1.4). Starting values follow the
    // proposal's §2.3 examples; expected to move during the nkr1.7
    // playtest/tuning gate — tune here (and later via the game_rulesets
    // publish flow), never in code.
    // Round 6, revised: ONE POINT PER LETTER, flat. Letter-value pricing
    // was fairer in theory but unreadable in practice — "10 letters left
    // −16" makes the player do arithmetic to find out why, and a scoring
    // rule you have to decode is a worse rule than a slightly blunter one
    // you can read at a glance. Flat 1 gives "10 letters left −10", which
    // needs no explanation at all. (letter-value is still selectable —
    // ?wbvariant=penalty-letters — if the playtest misses the nuance.)
    unusedPenaltyMode: 'flat',
    // Round 8 (playtest, user call): the unused-letter penalty is OFF.
    // "I think we turn off the lose a point for every remaining letter piece.
    // I think it isn't really necessary." It made every round end on a
    // subtraction, and the board-clear bonus plus the theme hunt already
    // reward emptying the board — the stick was redundant next to two carrots.
    // Ruleset-first, so a playtest can turn it back on without a code change.
    unusedLetterPenalty: 0,
    // Round 10: the carrot that replaces the removed stick. A near-clear is
    // skilful and used to be worth ~2 points; it is now worth most of this.
    boardProgressBonus: 100,
    unusedPenaltyMaxPctOfBanked: 50,
    boardClearedBonus: 100,
    // Round 10: 2 -> 1.4. Doubling made chained clears (100/200/400/800) dwarf
    // every other source of points, so the top of the board measured stamina
    // at chaining rather than word skill — 1st and 3rd differed 4x on score
    // but only 2.3x on word points. 1.4 gives 100/140/196/274: still clearly
    // worth chaining, no longer the only thing that counts.
    clearedBonusGrowth: 1.4,
    timeSavedMultiplierPerSec: 0.05,
    reshufflesPerGame: 3,
    shuffleBreaksCombo: true,
    nextBoardBreaksCombo: true,
    packingMinWordLength: 3,
    packingMaxWordLength: 8,
    // Gold is par itself — matching the solver's own decomposition, which
    // should stay genuinely hard. Silver and Bronze are set where a decent
    // and a first-time round actually land, so most players finish a round
    // having earned something.
    parMedals: [
      { name: 'Gold', pct: 100 },
      { name: 'Silver', pct: 65 },
      { name: 'Bronze', pct: 35 },
    ],
    // Prototype mechanics (nkr1.9) — every flag OFF in the base ruleset;
    // the P1.8 prototype (and the ?wbvariant dev override) enables them
    // per test bucket. Values are research-derived starting points for the
    // nkr1.7 playtest, not tuned truths.
    variants: {
      chainEnabled: false,
      chainMode: 'hot-zone',
      chainWindowSec: 4,
      chainStreakBonus: 1,
      revealEnabled: false,
      revealTopMissed: 5,
      ranksEnabled: false,
      rankTiers: [
        { name: 'Good', pct: 2 },
        { name: 'Solid', pct: 5 },
        { name: 'Great', pct: 9 },
        { name: 'Amazing', pct: 14 },
        { name: 'Genius', pct: 20 },
      ],
      comboMode: 'streak-tiers',
      comboDecaySec: 4,
      comboChargeDivisor: 8,
      // Proximity scoring (6km0) — off until the playtest rates it.
      // +15%/tile capped at +60% makes a fully-hot 4-letter word worth
      // ~1.6× without eclipsing the length bonus.
      proximityEnabled: false,
      proximityBonusPct: 15,
      proximityMaxPct: 60,
      blindSubmitEnabled: false,
      themedBoardsEnabled: true,
      // Was 'survey' for the closed beta (6km0): a survey question bought the
      // replay. Turned off 2026-08-05 for the beta exit.
      //
      // The gate sat on Play Again — the single control that decides whether
      // anyone comes back — and interrupted EVERY replay with a five-option
      // poll headed "You're playing an early build". 21 questions in the bank.
      // That is a reasonable trade while a handful of known testers are being
      // mined for answers, and an unreasonable one the moment strangers arrive:
      // the cost is paid by the most engaged players, most often.
      //
      // Exactly the config change the enum's doc comment describes, and the
      // ungated path is already the default branch, so nothing else moves.
      // Restore with ?wbvariant=gate-survey when a question genuinely needs
      // answering.
      replayGate: 'none',
      themeHidden: true,
      // Three (was two, 2026-08-06, the owner's call after playing it).
      //
      // The count is DAY-CUMULATIVE, not per-round — words found on any
      // earlier run today count toward it — so on a replay the name can be
      // earned before the new round has produced anything. Two made that
      // common enough that the name often arrived while the puzzle still felt
      // open; three keeps it earned.
      //
      // Still the number most worth tuning from real answers rather than
      // taste, which is what survey question theme-reveal-timing-v1 is for.
      themeRevealAfter: 3,
      comboEnabled: false,
      // Three: a streak worth protecting exists, so the "save your streak"
      // pitch is true rather than a nag. See the field's doc comment.
      accountPitchMinStreak: 3,
    },
  },
};

// ───────────────────────────── Type guards ─────────────────────────────

/**
 * Minimal runtime shape check — confirms the top-level keys exist and
 * have the right primitive kinds. This is NOT a deep validator; once
 * Phase 1.3 brings DB-loaded rulesets into the picture, swap this for
 * a Zod schema that fully validates ranges + enum values.
 */
export function isGameRuleset(value: unknown): value is GameRuleset {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.name === 'string' &&
    v.schemaVersion === 1 &&
    typeof v.gameKind === 'string' &&
    typeof v.grid === 'object' &&
    typeof v.timing === 'object' &&
    typeof v.scoring === 'object' &&
    typeof v.modifierSpawn === 'object' &&
    typeof v.matchRule === 'object'
  );
}
